import { type ReactElement, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ErrorBoundary } from '../observability';
import { isModuleMigrated } from './migration-bridge';

export interface ModuleGateProps {
  readonly flagKey: string;
  /** Slug do módulo; vira o `escopo` do erro de render da página. */
  readonly scope: string;
  readonly page: ReactElement;
  readonly fallback: ReactElement;
}

interface ModuleEntry {
  readonly flagKey: string;
  readonly migrated: boolean;
}

function enterModule(flagKey: string): ModuleEntry {
  return { flagKey, migrated: isModuleMigrated(flagKey) };
}

/**
 * Escolhe entre a página React e a folha Angular uma vez por entrada no módulo.
 *
 * O React Router reusa esta instância entre rotas do mesmo shell, então um estado
 * inicializado só na montagem nunca seria reavaliado ao trocar de módulo. A decisão é
 * rederivada quando a chave muda e mantida enquanto ela é a mesma: um flip remoto não
 * troca a tela no meio de um fluxo do módulo (lista em React, formulário em Angular).
 * Sem `key` no gate, a folha do ramo fallback preserva a instância e o próprio host
 * continua sendo o único a decidir quando remontar.
 */
export function ModuleGate({ flagKey, scope, page, fallback }: ModuleGateProps): JSX.Element {
  const { pathname } = useLocation();
  const [entry, setEntry] = useState(() => enterModule(flagKey));

  let current = entry;
  if (entry.flagKey !== flagKey) {
    // Estado derivado de prop: o React descarta esta passada e re-renderiza na hora com a
    // entrada nova, então a leitura da flag acontece uma vez por troca de módulo.
    current = enterModule(flagKey);
    setEntry(current);
  }

  // O boundary reinicia a cada mudança de pathname, mas só age depois de um erro: sem
  // falha, trocar de rota não remonta o conteúdo.
  return (
    <ErrorBoundary scope={scope} resetKey={pathname}>
      {current.migrated ? page : fallback}
    </ErrorBoundary>
  );
}
